import { useMemo, useState } from "react";
import { CalendarDays, Download, FileSpreadsheet, X } from "lucide-react";

import {
  buildClientReportRows,
  buildCsvExportRows,
  buildDailyReportRows,
  entrySpanSeconds,
  filterEntriesByDateRange,
  formatDurationCompact,
  renderCsv,
  toLocalDayKey,
} from "@zentrack/domain";
import {
  Button,
  Calendar,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  MetricTile,
  PageHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@zentrack/ui";

import { useWebApp } from "../app/app-provider";
import { downloadText } from "../lib/browser-download";

interface CalendarDateRange {
  from: Date | undefined;
  to?: Date | undefined;
}

interface DateFilter {
  from: string | null;
  to: string | null;
}

function formatDateLabel(dayKey: string): string {
  const [year, month, day] = dayKey.split("-").map(Number);
  return new Date(year!, month! - 1, day!).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function filterLabel(filter: DateFilter): string {
  if (filter.from && filter.to) {
    return `${formatDateLabel(filter.from)} – ${formatDateLabel(filter.to)}`;
  }
  if (filter.from) return `From ${formatDateLabel(filter.from)}`;
  if (filter.to) return `Until ${formatDateLabel(filter.to)}`;
  return "All time";
}

export function ReportsRoute() {
  const { snapshot } = useWebApp();
  const [dateFilter, setDateFilter] = useState<DateFilter>({ from: null, to: null });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarRange, setCalendarRange] = useState<CalendarDateRange | undefined>(undefined);

  const filteredEntries = useMemo(
    () =>
      snapshot ? filterEntriesByDateRange(snapshot.timeEntries, dateFilter.from, dateFilter.to) : [],
    [snapshot?.timeEntries, dateFilter],
  );

  const filteredAppUsages = useMemo(() => {
    if (!snapshot) return [];
    if (!dateFilter.from && !dateFilter.to) return snapshot.appUsages;
    const entryIds = new Set(filteredEntries.map((e) => e.id));
    return snapshot.appUsages.filter((u) => entryIds.has(u.timeEntryId));
  }, [snapshot?.appUsages, filteredEntries, dateFilter]);

  const reports = useMemo(() => {
    if (!snapshot) return null;
    const dailyRows = buildDailyReportRows(filteredEntries, filteredAppUsages);
    const clientRows = buildClientReportRows(
      snapshot.clients,
      snapshot.projects,
      filteredEntries,
    );

    const projectsById = new Map(snapshot.projects.map((p) => [p.id, p]));
    const clientsById = new Map(snapshot.clients.map((c) => [c.id, c]));
    const projectTotals = new Map<string, number>();
    for (const entry of filteredEntries) {
      projectTotals.set(
        entry.projectId,
        (projectTotals.get(entry.projectId) ?? 0) + entry.countedDurationSeconds,
      );
    }
    const projectRows = [...projectTotals.entries()]
      .map(([projectId, countedDurationSeconds]) => {
        const project = projectsById.get(projectId);
        const client = project ? clientsById.get(project.clientId) : undefined;
        return {
          projectId,
          projectName: project?.name ?? "Unknown Project",
          clientName: client?.name ?? "Unknown Client",
          countedDurationSeconds,
        };
      })
      .sort((a, b) => b.countedDurationSeconds - a.countedDurationSeconds);

    const totalTrackedDurationSeconds = filteredEntries.reduce(
      (total, entry) => total + entry.countedDurationSeconds,
      0,
    );
    const hasFilteredSessions = filteredEntries.some(
      (entry) => entry.countedDurationSeconds < entrySpanSeconds(entry),
    );

    return {
      dailyRows,
      clientRows,
      projectRows,
      totalTrackedDurationSeconds,
      hasFilteredSessions,
    };
  }, [snapshot, filteredEntries, filteredAppUsages]);

  if (!snapshot) {
    return null;
  }

  const handleExport = () => {
    if (!snapshot) return;

    const rows = buildCsvExportRows(filteredEntries, filteredAppUsages, snapshot.projects, snapshot.clients);
    const csv = renderCsv(rows);
    const fileName = `zentrack-export-${toLocalDayKey(new Date())}.csv`;
    downloadText(csv, fileName, "text/csv");
    toast(`CSV exported as ${fileName}.`);
  };

  const applyCalendarRange = (range: CalendarDateRange | undefined) => {
    setCalendarRange(range);
    setDateFilter({
      from: range?.from ? toLocalDayKey(range.from) : null,
      to: range?.to ? toLocalDayKey(range.to) : null,
    });
    if (range?.from && range?.to) {
      setCalendarOpen(false);
    }
  };

  const clearDateFilter = () => {
    setDateFilter({ from: null, to: null });
    setCalendarRange(undefined);
  };

  const isFiltered = Boolean(dateFilter.from || dateFilter.to);

  return (
    <div className="flex flex-col gap-5 pb-10">
      <PageHeader
        actions={
          <Button onClick={handleExport} type="button" variant="outline">
            <Download className="size-4" />
            Export CSV
          </Button>
        }
        description="Review totals and export CSV."
        title="Reports"
      />

      <div className="flex items-center gap-2">
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger
            render={
              <Button type="button" variant="outline" className="gap-2">
                <CalendarDays className="size-4" />
                {filterLabel(dateFilter)}
              </Button>
            }
          />
          <PopoverContent align="start" className="p-3">
            <Calendar
              mode="range"
              selected={calendarRange as { from: Date | undefined; to?: Date | undefined }}
              onSelect={(range) => applyCalendarRange(range as CalendarDateRange | undefined)}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
        {isFiltered ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearDateFilter}
            className="gap-1 text-muted-foreground"
          >
            <X className="size-3.5" />
            Clear
          </Button>
        ) : null}
      </div>

      {snapshot.timeEntries.length === 0 ? (
        <EmptyState
          action={
            <Button onClick={handleExport} type="button" variant="outline">
              <Download className="size-4" />
              Export header-only CSV
            </Button>
          }
          description="Track at least one finished session before reviewing totals. You can still export a header-only CSV right now."
          icon={FileSpreadsheet}
          title="No reports yet"
        />
      ) : filteredEntries.length === 0 ? (
        <EmptyState
          action={
            <Button type="button" variant="outline" onClick={clearDateFilter}>
              Clear filter
            </Button>
          }
          description="No entries fall within the selected date range. Try a different range or clear the filter."
          icon={CalendarDays}
          title="No entries in range"
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <MetricTile
              detail={
                reports?.hasFilteredSessions
                  ? "Excludes ignored software time."
                  : "All saved tracked time."
              }
              title="Counted time"
              tone="accent"
              value={formatDurationCompact(reports?.totalTrackedDurationSeconds ?? 0)}
            />
            <MetricTile detail="Finished entries in report." title="Sessions" value={`${filteredEntries.length}`} />
            <MetricTile
              detail="Clients with tracked time."
              title="Clients"
              value={`${reports?.clientRows.length ?? 0}`}
            />
            <MetricTile
              detail="Projects in saved entries."
              title="Projects"
              value={`${reports?.projectRows.length ?? 0}`}
            />
          </div>

          <ReportTable
            title="By client"
            columns={["Client", "Projects", "Time"]}
            rows={(reports?.clientRows ?? []).map((row) => ({
              cells: [
                row.clientName,
                `${row.projectCount} ${row.projectCount === 1 ? "project" : "projects"}`,
                formatDurationCompact(row.countedDurationSeconds),
              ],
              numericLast: true,
            }))}
          />

          <ReportTable
            title="By project"
            columns={["Project", "Client", "Time"]}
            rows={(reports?.projectRows ?? []).map((row) => ({
              cells: [row.projectName, row.clientName, formatDurationCompact(row.countedDurationSeconds)],
              numericLast: true,
            }))}
          />

          <ReportTable
            title="By day"
            columns={["Day", "Sessions", "Time"]}
            rows={(reports?.dailyRows ?? []).map((row) => ({
              cells: [
                formatDateLabel(row.date),
                `${row.sessionCount} ${row.sessionCount === 1 ? "session" : "sessions"}`,
                formatDurationCompact(row.countedDurationSeconds),
              ],
              numericLast: true,
            }))}
          />
        </>
      )}
    </div>
  );
}

interface ReportRow {
  cells: string[];
  numericLast?: boolean;
}

function ReportTable({
  rows,
  title,
  columns,
}: {
  rows: ReportRow[];
  title: string;
  columns: string[];
}) {
  return (
    <Card>
      <CardHeader className="border-b border-border/60 pb-3">
        <p className="text-sm font-medium text-foreground">{title}</p>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column}
                  className={column === columns[columns.length - 1] ? "text-right" : undefined}
                >
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={`${title}-${index}`}>
                {row.cells.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    className={
                      row.numericLast && cellIndex === row.cells.length - 1
                        ? "text-right tabular-nums"
                        : undefined
                    }
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
